// /Users/rd/CaseManageVue/src/firebase.js

import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getAuth, GoogleAuthProvider } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'

// Firebase configuration — loaded from environment variables per school.
// Each school deployment must provide its own .env.production file.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'appId']
for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    throw new Error(
      `Missing required Firebase config: VITE_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}. ` +
        'Ensure a .env.production file exists with all VITE_FIREBASE_* variables.'
    )
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// ─── APP CHECK ───────────────────────────────────────────────────────────────
// App Check proves requests come from THIS app (not a script or cloned client).
// It is a layer ON TOP of Firestore/Storage rules — never a replacement for them.
//
// Opt-in: only initializes when VITE_RECAPTCHA_ENTERPRISE_SITE_KEY is set, so
// existing deployments keep working until you (1) register the site key and
// (2) turn on enforcement per service in the Firebase console. Until enforced,
// running this is harmless.
//
// Local/dev: set VITE_APPCHECK_DEBUG_TOKEN to a debug token registered in the
// console, or leave it unset and set it to `true` to have the SDK print a fresh
// debug token to the console on first load (register that token, then reload).
let appCheck = null
const appCheckSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY
if (appCheckSiteKey) {
  try {
    const isDev = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development'
    if (isDev) {
      // Must be set BEFORE initializeAppCheck. `true` makes the SDK log a debug
      // token; an explicit token (e.g. for CI) is used verbatim if provided.
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true
    })
    console.log('🛡️ App Check initialized')
  } catch (error) {
    console.error('❌ App Check initialization failed:', error)
  }
} else {
  console.warn(
    '⚠️ App Check not configured (VITE_RECAPTCHA_ENTERPRISE_SITE_KEY missing). ' +
      'Backend resources have no client-attestation layer until this is set and enforced.'
  )
}

// Auth persistence is the Firebase web default: local (IndexedDB), shared
// across tabs, and restored on refresh. Do not call setPersistence() here.
// That call is async and races the first auth read; on a reload it can clear
// the restored session and send the user to the login page.
const auth = getAuth(app)
const db = getFirestore(app)
const storage = getStorage(app)
// Region must match where Cloud Functions are deployed (see functions config).
// Defaults to us-central1 when VITE_FIREBASE_REGION is unset.
const functions = getFunctions(app, import.meta.env.VITE_FIREBASE_REGION || 'us-central1')

// Enable debug logging in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Firebase debug mode enabled')
  import('firebase/firestore')
    .then(() => {
      console.log('🔧 Firestore persistence enabled')
    })
    .catch((err) => {
      console.log('🔧 Firestore persistence not enabled:', err)
    })
}

// Set up Google Auth Provider
const googleProvider = new GoogleAuthProvider()

// Legacy "Gmail at login" scope detection. This used to probe Google's
// tokeninfo endpoint to decide whether to request the gmail.compose scope at
// sign-in. That heuristic was unreliable (it always 400'd) and is now obsolete:
// email is sent server-side via the sendBulkEmail Cloud Function. Kept as a
// no-op so the legacy status panel and callers don't break.
const checkAndAddGmailScope = async () => false

console.log('🔥 Connected to Firebase project:', firebaseConfig.projectId)

export { auth, db, storage, functions, appCheck, googleProvider, checkAndAddGmailScope }
